import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { useIntl } from 'react-intl'
import { messages } from '../../messages'
import { Leg, LoadMetadata, TransportModeId } from '../plannerTypes'
import { PlannerAction } from '../state/journeyPlannerReducer'
import RouteSection from './RouteSection'

type Service = {
  id: number
  name: string
}

type LegSectionProps = {
  leg: Leg
  legIndex: number
  loadMetadataById: Record<number, LoadMetadata>
  dispatch: React.Dispatch<PlannerAction>
  canRemove: boolean
  tenantId: number
  onSplitRouteClick: (legIndex: number, routeIndex: number) => void
  onMergeRouteClick: (legIndex: number) => void
  onAddDestinationClick: (legIndex: number, routeIndex: number, destIndex: number) => void
  onEditActionsClick: (legIndex: number, routeIndex: number, destIndex: number) => void
  previousLegLastDate?: Dayjs
  services?: Service[]
}

const LegSection = ({ leg, legIndex, loadMetadataById, dispatch, canRemove, tenantId, onSplitRouteClick, onMergeRouteClick, onAddDestinationClick, onEditActionsClick, previousLegLastDate, services = [] }: LegSectionProps) => {
  const { formatMessage } = useIntl()

  const handleTransportModeChange = (event: SelectChangeEvent<number>) => {
    dispatch({
      type: 'SET_LEG_TRANSPORT_MODE',
      legIndex,
      transportModeId: event.target.value as number,
    })
  }

  return (
    <Card variant="outlined" sx={{ position: 'relative' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 'bold',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                px: 2,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              {formatMessage(messages.legNumber, { number: leg.sequence })}
            </Typography>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id={`transport-mode-label-${legIndex}`}>
                {formatMessage(messages.transportModeName)}
              </InputLabel>
              <Select
                labelId={`transport-mode-label-${legIndex}`}
                value={leg.transportModeId || ''}
                label={formatMessage(messages.transportModeName)}
                onChange={handleTransportModeChange}
              >
                <MenuItem value="">
                  <em>{formatMessage(messages.selectTransportMode)}</em>
                </MenuItem>
                <MenuItem value={TransportModeId.Road}>{formatMessage(messages.road)}</MenuItem>
                <MenuItem value={TransportModeId.Sea}>{formatMessage(messages.sea)}</MenuItem>
                <MenuItem value={TransportModeId.Air}>{formatMessage(messages.air)}</MenuItem>
                <MenuItem value={TransportModeId.Rail}>{formatMessage(messages.rail)}</MenuItem>
              </Select>
            </FormControl>

            {!leg.fulfilmentRequired && (
              <Typography
                variant="caption"
                sx={{
                  bgcolor: 'warning.light',
                  color: 'warning.contrastText',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                {formatMessage(messages.fulfilmentNotRequired)}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={leg.routes.length < 2}
              onClick={() => onMergeRouteClick(legIndex)}
              data-testid={`merge-routes-button-${legIndex}`}
              sx={{
                bgcolor: leg.routes.length >= 2 ? 'primary.main' : 'grey.400',
                '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' },
              }}
            >
              {formatMessage(messages.merge)}
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {leg.routes.map((route, routeIndex) => {
            // Count unique loads in this route to determine if split is available
            const loadIds = new Set<number>()
            route.destinations.forEach((dest) => {
              dest.loadActions.forEach((action) => {
                loadIds.add(action.loadId)
              })
            })
            const canSplit = loadIds.size > 1

            // All routes within a leg are parallel (not sequential), so they all use
            // the previous leg's last date as their minimum constraint.
            // Routes within the same leg can have independent date ranges since they
            // represent different carriers/paths handling loads simultaneously.
            const previousRouteLastDate: Dayjs | undefined = previousLegLastDate

            return (
              <RouteSection
                key={route.id}
                route={route}
                legIndex={legIndex}
                routeIndex={routeIndex}
                loadMetadataById={loadMetadataById}
                dispatch={dispatch}
                canRemove={leg.routes.length > 1}
                canSplit={canSplit}
                onSplitClick={() => onSplitRouteClick(legIndex, routeIndex)}
                onAddDestinationClick={(destIndex) => onAddDestinationClick(legIndex, routeIndex, destIndex)}
                onEditActionsClick={(destIndex) => onEditActionsClick(legIndex, routeIndex, destIndex)}
                tenantId={tenantId}
                transportModeId={leg.transportModeId}
                previousRouteLastDate={previousRouteLastDate}
                services={services}
              />
            )
          })}
        </Box>

      </CardContent>
    </Card>
  )
}

export default LegSection
